import { useRef } from "react";

import { haptic } from "@/lib/haptics";

/**
 * Long-press recogniser for touch lists: the native "context menu" gesture.
 *
 *   const press = useLongPress();
 *   <li {...press.bind(() => openMenu(id))}
 *       onClick={() => { if (press.consumeClick()) return; open(id); }} />
 *
 * Fires after `ms` unless the finger lifts first or travels more than `slop` px (that is a
 * scroll). Mouse input is ignored: desktop has right-click and always-visible buttons.
 */
export default function useLongPress({ ms = 480, slop = 8 } = {}) {
  const st = useRef({ timer: 0, x: 0, y: 0, fired: false });

  const clear = () => {
    window.clearTimeout(st.current.timer);
    st.current.timer = 0;
  };

  const bind = (onLongPress) => ({
    onPointerDown: (e) => {
      if (e.pointerType === "mouse") return;
      clear();
      st.current.fired = false;
      st.current.x = e.clientX;
      st.current.y = e.clientY;
      st.current.timer = window.setTimeout(() => {
        st.current.timer = 0;
        st.current.fired = true;
        haptic("medium");
        onLongPress();
      }, ms);
    },
    onPointerMove: (e) => {
      if (st.current.timer && Math.hypot(e.clientX - st.current.x, e.clientY - st.current.y) > slop) clear();
    },
    onPointerUp: clear,
    onPointerCancel: clear,
    onPointerLeave: clear,
    // Android raises a context menu on long-press: ours replaces it.
    onContextMenu: (e) => e.preventDefault(),
  });

  /** True, once, for the click that trails a long-press: the caller should ignore it. */
  const consumeClick = () => {
    const fired = st.current.fired;
    st.current.fired = false;
    return fired;
  };

  return { bind, consumeClick };
}
